import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AcceptInvitation = () => {
  const [loading, setLoading] = useState(true);
  const [invitationData, setInvitationData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyInvitation = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/team/verify-invitation/${token}`);
        setInvitationData({ ...response.data, token });
        setLoading(false);
      } catch (error) {
        setError(error.response?.data?.detail || 'Invalid or expired invitation');
        setLoading(false);
      }
    };

    verifyInvitation();
  }, []);

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('token', invitationData.token);
      formData.append('password', password);

      const response = await axios.post(`${BACKEND_URL}/api/team/accept-invitation`, formData);
      
      localStorage.setItem('token', response.data.access_token);
      window.location.href = '/';
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to accept invitation');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitationData) {
    const isExpired = error.includes('expired') || error.includes('no longer valid');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">{isExpired ? '⏰' : '⚠️'}</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {isExpired ? 'Invitation Expired' : 'Invalid Invitation'}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          
          {isExpired && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-medium text-blue-900 mb-2">Need a new invitation?</p>
              <p className="text-sm text-gray-700">
                Contact your team administrator and ask them to:
              </p>
              <ol className="text-sm text-gray-700 list-decimal ml-4 mt-2 space-y-1">
                <li>Go to the <strong>Team</strong> page</li>
                <li>Click on your name to edit</li>
                <li>Click the <strong>"Resend Invitation"</strong> button</li>
              </ol>
            </div>
          )}
          
          <a href="/" className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium">
            Return to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome!</h2>
          <p className="text-gray-600">You've been invited to join <strong>{invitationData.business_name}</strong></p>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-700"><strong>Name:</strong> {invitationData.team_member_name}</p>
          <p className="text-sm text-gray-700"><strong>Email:</strong> {invitationData.email}</p>
          <p className="text-sm text-gray-700"><strong>Role:</strong> {invitationData.role}</p>
        </div>

        <form onSubmit={handleAcceptInvitation} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Create Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Confirm your password"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? 'Creating Account...' : 'Accept Invitation & Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvitation;
